import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import '../../assets/styles/infrastructure_leasing_modal.scss';
import StorageService from '../../services/StorageService';
import {
  dispatchInfrastructureNotificationsRefresh,
  submitPublishedInfrastructureEnquiry,
} from '../../services/infrastructureLeasingListing.service';
import { URGENCY_OPTIONS } from '../../constants/infrastructureLeasingUrgencyOptions';

const PROPERTY_TYPE_LABELS = {
  office_space: 'Office Space',
  retail_shop: 'Retail Shop',
  warehouse: 'Warehouse',
  factory: 'Factory',
  industrial_shed: 'Industrial Shed',
  coworking_space: 'Coworking',
  land: 'Land',
  cold_storage: 'Cold Storage',
  agricultural_land: 'Agricultural',
  hospitality_property: 'Hospitality',
};

function str(v) {
  if (v == null) return '';
  return String(v).trim();
}

function assetTypeLabel(listing) {
  if (!listing) return '—';
  if (listing.listingType === 'machinery') {
    return 'Equipment & machinery';
  }
  const cat = str(listing.categoryLabel);
  if (cat) return cat;
  const pt = listing.propertyType;
  if (pt && PROPERTY_TYPE_LABELS[pt]) return PROPERTY_TYPE_LABELS[pt];
  return pt ? str(pt).replace(/_/g, ' ') : 'Real estate & infrastructure';
}

function locationLine(listing) {
  if (!listing) return '—';
  const line = str(listing.locationLine);
  if (line && line !== '—') return line;
  const parts = [str(listing.cityName), str(listing.stateName), str(listing.countryName)].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

function pickUserContact() {
  const u = StorageService.getUser();
  if (!u || typeof u !== 'object') {
    return { name: '', email: '', phone: '', company: '' };
  }
  return {
    name: str(u.name),
    email: str(u.email),
    phone: str(u.phone),
    company: str(u.company_name),
  };
}

/** Basic email shape check (matches typical HTML5 email expectations). */
function isValidEmail(s) {
  const t = str(s);
  if (!t) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

function digitCount(s) {
  return String(s ?? '').replace(/\D/g, '').length;
}

/**
 * Send enquiry form in a Bootstrap 5 modal (scrollable, centered).
 */
export default function InfrastructureLeasingSendEnquiryModal({ open, onClose, listing, listingId, onEnquirySent }) {
  const suffix = listingId != null ? String(listingId) : 'x';

  const [startDate, setStartDate] = useState('');
  const [durationMonths, setDurationMonths] = useState('');
  const [budget, setBudget] = useState('');
  const [areaRequired, setAreaRequired] = useState('');
  const [message, setMessage] = useState('');
  const [urgency, setUrgency] = useState('flexible');
  const [attachment, setAttachment] = useState(null);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactCompany, setContactCompany] = useState('');

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !listing) return;
    const c = pickUserContact();
    setContactName(c.name);
    setContactEmail(c.email);
    setContactPhone(c.phone);
    setContactCompany(c.company);
    setStartDate('');
    setDurationMonths('');
    setBudget('');
    setAreaRequired('');
    setMessage('');
    setUrgency('flexible');
    setAttachment(null);
    setErrors({});
  }, [open, listing]);

  const dismissError = (field) => {
    setErrors((prev) => {
      if (prev[field] == null) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /** Returns a map of field key → error message (empty object if valid). */
  const buildValidationErrors = () => {
    const e = {};

    if (!str(startDate)) {
      e.startDate = 'Start date is required.';
    }

    const dmRaw = String(durationMonths ?? '').trim();
    const dm = parseInt(dmRaw, 10);
    if (dmRaw === '' || Number.isNaN(dm) || dm < 1) {
      e.durationMonths = 'Enter duration in months (at least 1).';
    }

    const msg = str(message);
    if (!msg) {
      e.message = 'Requirement message is required.';
    } else if (msg.length < 20) {
      e.message = 'Please enter at least 20 characters.';
    }

    if (!str(contactName)) {
      e.contactName = 'Name is required.';
    }

    const em = str(contactEmail);
    if (!em) {
      e.contactEmail = 'Email is required.';
    } else if (!isValidEmail(em)) {
      e.contactEmail = 'Enter a valid email address.';
    }

    const ph = str(contactPhone);
    if (!ph) {
      e.contactPhone = 'Phone number is required.';
    } else if (digitCount(ph) < 8) {
      e.contactPhone = 'Enter a valid phone number (at least 8 digits).';
    }

    return e;
  };

  const scrollToFirstError = (errMap) => {
    const order = ['startDate', 'durationMonths', 'message', 'contactName', 'contactEmail', 'contactPhone'];
    const idByField = {
      startDate: `ile-enq-start-${suffix}`,
      durationMonths: `ile-enq-duration-${suffix}`,
      message: `ile-enq-msg-${suffix}`,
      contactName: `ile-enq-name-${suffix}`,
      contactEmail: `ile-enq-email-${suffix}`,
      contactPhone: `ile-enq-phone-${suffix}`,
    };
    const first = order.find((k) => errMap[k]);
    if (!first) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(idByField[first]);
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      if (el && typeof el.focus === 'function') {
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      }
    });
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!listing) return;
    if (listing.viewerIsPublisher === true) {
      toast.info('You cannot send an enquiry on your own listing.');
      onClose();
      return;
    }
    if (listing.viewerHasSubmittedEnquiry === true) {
      toast.info('You have already sent an enquiry for this listing.');
      onClose();
      return;
    }

    const errMap = buildValidationErrors();
    setErrors(errMap);
    if (Object.keys(errMap).length > 0) {
      toast.warning('Please complete all required fields correctly.');
      scrollToFirstError(errMap);
      return;
    }

    const idNum = listingId == null || listingId === '' ? NaN : Number(listingId);
    if (!Number.isFinite(idNum) || idNum < 1) {
      toast.error('Missing listing reference. Please refresh and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const dm = parseInt(String(durationMonths).trim(), 10);
      const payload = {
        listingId: String(idNum),
        asset: {
          name: str(listing.title) || 'Untitled',
          type: assetTypeLabel(listing),
          location: locationLine(listing),
        },
        enquiry: {
          startDate: str(startDate),
          durationMonths: dm,
          budget: str(budget) || null,
          areaRequired: str(areaRequired) || null,
          message: str(message),
          urgency,
          attachment: attachment
            ? { name: attachment.name, size: attachment.size, type: attachment.type || '' }
            : null,
        },
        contact: {
          name: str(contactName),
          email: str(contactEmail),
          phone: str(contactPhone),
          company: str(contactCompany),
        },
      };
      await submitPublishedInfrastructureEnquiry(idNum, payload);
      toast.success('Your enquiry has been sent.');
      dispatchInfrastructureNotificationsRefresh();
      if (typeof onEnquirySent === 'function') {
        await onEnquirySent();
      }
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || 'Could not send enquiry.';
      if (status === 401) {
        toast.error('Please sign in to send an enquiry.');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Could not send enquiry.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const modalUi = (
    <div className="ile-modal-root">
      <div className="ile-modal-overlay" onClick={onClose} role="presentation">
        <div
          className="ile-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`ile-enq-modal-title-${suffix}`}
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ile-modal-panel__header">
            <button type="button" className="ile-modal-panel__close" onClick={onClose} aria-label="Close send enquiry">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
            <h2 className="ile-modal-panel__title" id={`ile-enq-modal-title-${suffix}`}>
              Send Enquiry
            </h2>
            <p className="ile-modal-panel__subtitle">Share your requirement with the seller</p>
          </div>

          <form className="ile-modal-panel__form" onSubmit={handleSubmit} noValidate>
            <div className="ile-modal-panel__body">
              {listing && (
                <>
                  <div className="ile-modal-block">
                    <h3 className="ile-modal-block__title">Asset</h3>
                    <dl className="row mb-0 small">
                      <dt className="col-sm-4 text-secondary">Asset name</dt>
                      <dd className="col-sm-8 mb-1">{str(listing.title) || 'Untitled'}</dd>
                      <dt className="col-sm-4 text-secondary">Asset type</dt>
                      <dd className="col-sm-8 mb-1">{assetTypeLabel(listing)}</dd>
                      <dt className="col-sm-4 text-secondary mb-0">Location</dt>
                      <dd className="col-sm-8 mb-0">{locationLine(listing)}</dd>
                    </dl>
                  </div>

                  <div className="ile-modal-block">
                    <h3 className="ile-modal-block__title">Enquiry</h3>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-start-${suffix}`}>
                              Start date <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`ile-enq-start-${suffix}`}
                              type="date"
                              className={`input-pill ${errors.startDate ? 'is-invalid' : ''}`}
                              value={startDate}
                              onChange={(e) => {
                                setStartDate(e.target.value);
                                dismissError('startDate');
                              }}
                              aria-invalid={errors.startDate ? 'true' : 'false'}
                            />
                            {errors.startDate && <span className="ile-modal-field-error">{errors.startDate}</span>}
                          </div>
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-duration-${suffix}`}>
                              Duration (months) <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`ile-enq-duration-${suffix}`}
                              type="number"
                              min={1}
                              step={1}
                              className={`input-pill ${errors.durationMonths ? 'is-invalid' : ''}`}
                              value={durationMonths}
                              onChange={(e) => {
                                setDurationMonths(e.target.value);
                                dismissError('durationMonths');
                              }}
                              placeholder="e.g. 12"
                              aria-invalid={errors.durationMonths ? 'true' : 'false'}
                            />
                            {errors.durationMonths && (
                              <span className="ile-modal-field-error">{errors.durationMonths}</span>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-budget-${suffix}`}>
                              Budget (optional)
                            </label>
                            <input
                              id={`ile-enq-budget-${suffix}`}
                              type="text"
                              className="input-pill"
                              value={budget}
                              onChange={(e) => setBudget(e.target.value)}
                              placeholder="₹ or range"
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-area-${suffix}`}>
                              Area required (optional)
                            </label>
                            <input
                              id={`ile-enq-area-${suffix}`}
                              type="text"
                              className="input-pill"
                              value={areaRequired}
                              onChange={(e) => setAreaRequired(e.target.value)}
                              placeholder="e.g. sq. ft."
                            />
                          </div>
                        </div>

                        <div className="mt-2">
                          <label className="ile-form-label" htmlFor={`ile-enq-msg-${suffix}`}>
                            Requirement message <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id={`ile-enq-msg-${suffix}`}
                            className={`input-pill ${errors.message ? 'is-invalid' : ''}`}
                            rows={4}
                            value={message}
                            onChange={(e) => {
                              setMessage(e.target.value);
                              dismissError('message');
                            }}
                            placeholder="Describe what you need (min. 20 characters)."
                            aria-invalid={errors.message ? 'true' : 'false'}
                          />
                          {errors.message && <span className="ile-modal-field-error">{errors.message}</span>}
                        </div>

                        <div className="mt-2">
                          <label className="ile-form-label" htmlFor={`ile-enq-urgency-${suffix}`}>
                            Urgency
                          </label>
                          <select
                            id={`ile-enq-urgency-${suffix}`}
                            className="input-pill"
                            value={urgency}
                            onChange={(e) => setUrgency(e.target.value)}
                          >
                            {URGENCY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="mt-2">
                          <label className="ile-form-label" htmlFor={`ile-enq-file-${suffix}`}>
                            Attachment (optional)
                          </label>
                          <input
                            id={`ile-enq-file-${suffix}`}
                            type="file"
                            className="input-pill"
                            onChange={(e) =>
                              setAttachment(e.target.files && e.target.files[0] ? e.target.files[0] : null)
                            }
                          />
                          {attachment && <div className="ile-modal-field-hint">{attachment.name}</div>}
                        </div>
                  </div>

                  <div className="ile-modal-block">
                    <h3 className="ile-modal-block__title">Your contact</h3>
                        <div className="row g-2">
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-name-${suffix}`}>
                              Name <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`ile-enq-name-${suffix}`}
                              type="text"
                              className={`input-pill ${errors.contactName ? 'is-invalid' : ''}`}
                              value={contactName}
                              onChange={(e) => {
                                setContactName(e.target.value);
                                dismissError('contactName');
                              }}
                              autoComplete="name"
                              aria-invalid={errors.contactName ? 'true' : 'false'}
                            />
                            {errors.contactName && (
                              <span className="ile-modal-field-error">{errors.contactName}</span>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-email-${suffix}`}>
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`ile-enq-email-${suffix}`}
                              type="email"
                              className={`input-pill ${errors.contactEmail ? 'is-invalid' : ''}`}
                              value={contactEmail}
                              onChange={(e) => {
                                setContactEmail(e.target.value);
                                dismissError('contactEmail');
                              }}
                              autoComplete="email"
                              aria-invalid={errors.contactEmail ? 'true' : 'false'}
                            />
                            {errors.contactEmail && (
                              <span className="ile-modal-field-error">{errors.contactEmail}</span>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-phone-${suffix}`}>
                              Phone <span className="text-danger">*</span>
                            </label>
                            <input
                              id={`ile-enq-phone-${suffix}`}
                              type="tel"
                              className={`input-pill ${errors.contactPhone ? 'is-invalid' : ''}`}
                              value={contactPhone}
                              onChange={(e) => {
                                setContactPhone(e.target.value);
                                dismissError('contactPhone');
                              }}
                              autoComplete="tel"
                              aria-invalid={errors.contactPhone ? 'true' : 'false'}
                            />
                            {errors.contactPhone && (
                              <span className="ile-modal-field-error">{errors.contactPhone}</span>
                            )}
                          </div>
                          <div className="col-md-6">
                            <label className="ile-form-label" htmlFor={`ile-enq-company-${suffix}`}>
                              Company
                            </label>
                            <input
                              id={`ile-enq-company-${suffix}`}
                              type="text"
                              className="input-pill"
                              value={contactCompany}
                              onChange={(e) => setContactCompany(e.target.value)}
                              autoComplete="organization"
                            />
                          </div>
                        </div>
                  </div>
                </>
              )}
            </div>

            <div className="ile-modal-panel__footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="ile-modal-btn-primary" disabled={submitting || !listing}>
                {submitting ? 'Sending…' : 'Send Enquiry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalUi, document.body);
  }
  return modalUi;
}
